import { useState, useMemo, useRef } from 'react';
import cloneDeep from 'lodash.clonedeep';
import { useFormContext } from 'react-hook-form';
import Box from '@mui/material/Box';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import AddIcon from '@mui/icons-material/AddRounded';
import Button from '@components/Button';
import { Typography } from '@components/Typography';
import { SortableItem, DroppableEmptyZone } from '@components/DragAndDrop';
import AddDropdownHeader from '@components/AddDropdownHeader';
import LocalizeItemModal from '@features/LocalizeItemModal';
import ModalConfirm from '@components/Modal/ModalConfirm';
import { useTranslation } from '@hooks';
import { PREVIEW_LOCALES, LANGUAGES_MAP, getLocalizeLanguages } from '@constants/microsites';
import { getSurveyLanguageSet, visibleQuestionLanguages } from '@helpers/surveyLanguages';
import { QuestionType, QUESTION_TYPE_OPTIONS, FILTERABLE_TYPES } from '@constants/surveys';
import QuestionItem from './QuestionItem';
import PageDivider from './PageDivider';
import { getPreviousQuestionTitles, isQuestionValid } from './helpers';

// ── Styled components ──

// ── Main component ──

const Questions = ({ isView, account }) => {
    const [localizeModal, setLocalizeModal] = useState({ open: false, index: null, ctx: null });
    const [deleteModal, setDeleteModal] = useState({ open: false, key: null });

    const { t } = useTranslation();
    const { watch, setValue } = useFormContext();

    const questions = watch('questions');
    const pages = watch('pages');
    const satisfactionSurveyEnabled = watch('satisfactionSurveyEnabled');
    const customTextsEnabled = watch('customTextsEnabled');
    const texts = watch('texts');

    const availableLanguages = LANGUAGES_MAP[account.locale];
    const currentLocale = PREVIEW_LOCALES[account.locale];
    const languageSet = getSurveyLanguageSet(customTextsEnabled, texts);
    const languageOptions = languageSet
        ? getLocalizeLanguages(account.locale).filter(option => languageSet.includes(option.value))
        : undefined;

    const multiplePages = pages && pages.length > 1;

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

    // ── Data ──

    const dndEntries = Object.entries(questions);

    const { questionsByPage, keyToPage } = useMemo(() => {
        const byPage = {};
        const toPage = {};
        dndEntries.forEach(([key, questionLangs]) => {
            const page = questionLangs[0]?.page || 0;
            if (!byPage[page]) byPage[page] = [];
            byPage[page].push([key, questionLangs]);
            toPage[key] = page;
        });
        return { questionsByPage: byPage, keyToPage: toPage };
    }, [questions]);

    // ── Question handlers ──

    const onAddQuestion = type => {
        const questionsCopy = cloneDeep(questions);
        const lastKey = Object.keys(questionsCopy).length;
        const lastPage = pages?.length > 0 ? pages.length - 1 : 0;
        const newQuestions = {
            ...questionsCopy,
            [lastKey]: [
                {
                    title: '',
                    question: '',
                    language: availableLanguages[0],
                    type,
                    required: false,
                    page: lastPage,
                    options:
                        type === QuestionType.SINGLE_CHOICE || type === QuestionType.MULTI_SELECT
                            ? [{ id: 0, label: '', position: 0 }]
                            : [],
                    _isNew: true,
                    isOpen: true,
                },
            ],
        };
        setValue('questions', newQuestions, { shouldDirty: true });
    };

    const onClickQuestion = key => {
        const questionsCopy = cloneDeep(questions);
        questionsCopy[key][0].isOpen = !questionsCopy[key][0].isOpen;
        setValue('questions', questionsCopy, { shouldDirty: true });
    };

    const onChangeQuestion = (e, key, lang) => {
        const questionsCopy = cloneDeep(questions);
        if (e.target.name === 'title') {
            const oldTitle = questionsCopy[key][0]?.title;
            const newTitle = e.target.value;
            questionsCopy[key] = questionsCopy[key].map(q => ({ ...q, title: newTitle }));
            if (oldTitle && oldTitle !== newTitle) {
                Object.entries(questionsCopy).forEach(([k, langs]) => {
                    if (Number(k) <= Number(key)) return;
                    langs.forEach(q => {
                        if (!q.conditions) return;
                        q.conditions = q.conditions.map(c =>
                            c.questionTitle === oldTitle ? { ...c, questionTitle: newTitle } : c,
                        );
                    });
                });
            }
        } else {
            const questionIndex = questionsCopy[key].findIndex(l => l.language === lang);
            questionsCopy[key][questionIndex] = {
                ...questionsCopy[key][questionIndex],
                [e.target.name]: e.target.value,
            };
        }
        setValue('questions', questionsCopy, { shouldDirty: true });
    };

    const onOptionsChange = (options, key) => {
        const questionsCopy = cloneDeep(questions);
        const displayedLang =
            questionsCopy[key].find(q => q.language === currentLocale)?.language || questionsCopy[key][0]?.language;
        questionsCopy[key] = questionsCopy[key].map(q => {
            if (q.language === displayedLang) return { ...q, options };
            const existingOptions = q.options || [];
            const updatedOptions = options.map(opt => {
                const existing = existingOptions.find(e => e.id === opt.id);
                return existing ? { ...opt, label: existing.label } : { ...opt };
            });
            return { ...q, options: updatedOptions };
        });
        const changedTitle = questionsCopy[key][0]?.title;
        if (changedTitle) {
            const validIds = new Set(options.filter(o => !o.hidden).map(o => String(o.id)));
            Object.entries(questionsCopy).forEach(([k, langs]) => {
                if (Number(k) <= Number(key)) return;
                langs.forEach(q => {
                    if (!q.conditions) return;
                    q.conditions = q.conditions
                        .map(c => {
                            if (c.questionTitle !== changedTitle) return c;
                            const filtered = (c.values || []).filter(v => validIds.has(v));
                            return { ...c, values: filtered };
                        })
                        .filter(c => c.questionTitle !== changedTitle || c.values.length > 0);
                    if (q.conditions.length === 0) q.conditions = null;
                });
            });
        }
        setValue('questions', questionsCopy, { shouldDirty: true });
    };

    const onChangeRequired = (key, checked) => {
        const questionsCopy = cloneDeep(questions);
        questionsCopy[key] = questionsCopy[key].map(q => ({ ...q, required: checked }));
        setValue('questions', questionsCopy, { shouldDirty: true });
    };

    const onChangeIsGate = (key, checked) => {
        const questionsCopy = cloneDeep(questions);
        questionsCopy[key] = questionsCopy[key].map(q => ({
            ...q,
            isGate: checked,
            ...(checked ? { required: true, conditions: null } : {}),
        }));
        setValue('questions', questionsCopy, { shouldDirty: true });
    };

    const onChangeConditions = (key, conditions) => {
        const questionsCopy = cloneDeep(questions);
        questionsCopy[key] = questionsCopy[key].map(q => ({ ...q, conditions: conditions || null }));
        setValue('questions', questionsCopy, { shouldDirty: true });
    };

    const getPreviousFilterableQuestions = key => {
        const keyNum = Number(key);
        return Object.entries(questions)
            .filter(([k]) => Number(k) < keyNum)
            .map(([, langs]) => langs.find(l => l.language === currentLocale) || langs[0])
            .filter(q => q && FILTERABLE_TYPES.includes(q.type || QuestionType.RATING));
    };

    const onDeleteQuestion = key => setDeleteModal({ open: true, key });

    const onConfirmDelete = () => {
        const questionsCopy = cloneDeep(questions);
        const deletedTitle = questionsCopy[deleteModal.key]?.[0]?.title;
        delete questionsCopy[deleteModal.key];
        const copyUpdatedIndexes = Object.values(questionsCopy).reduce(
            (prev, value, index) => ({ ...prev, [index]: value }),
            {},
        );
        if (deletedTitle) {
            Object.values(copyUpdatedIndexes).forEach(langs => {
                langs.forEach(q => {
                    if (q.conditions) {
                        q.conditions = q.conditions.filter(c => c.questionTitle !== deletedTitle);
                        if (q.conditions.length === 0) q.conditions = null;
                    }
                });
            });
        }
        setValue('questions', copyUpdatedIndexes, { shouldDirty: true });
        setDeleteModal({ open: false, key: null });
    };

    // ── Page management ──

    const onAddPage = () => {
        const pagesCopy = cloneDeep(pages || [{}]);
        pagesCopy.push({});
        setValue('pages', pagesCopy, { shouldDirty: true });
    };

    const onDeletePage = pageIndex => {
        const pagesCopy = cloneDeep(pages || []);
        pagesCopy.splice(pageIndex, 1);
        const questionsCopy = cloneDeep(questions);
        Object.entries(questionsCopy).forEach(([key, langs]) => {
            const currentPage = langs[0]?.page || 0;
            if (currentPage === pageIndex) {
                questionsCopy[key] = langs.map(q => ({ ...q, page: Math.max(0, pageIndex - 1) }));
            } else if (currentPage > pageIndex) {
                questionsCopy[key] = langs.map(q => ({ ...q, page: currentPage - 1 }));
            }
        });
        if (pagesCopy.length <= 1) {
            Object.entries(questionsCopy).forEach(([key, langs]) => {
                questionsCopy[key] = langs.map(q => ({ ...q, page: 0 }));
            });
            setValue('pages', [{}], { shouldDirty: true });
        } else {
            setValue('pages', pagesCopy, { shouldDirty: true });
        }
        setValue('questions', questionsCopy, { shouldDirty: true });
    };

    // ── DnD handlers ──

    const preDragSnapshot = useRef(null);

    const handleDragStart = () => {
        preDragSnapshot.current = cloneDeep(questions);
    };

    const handleDragCancel = () => {
        if (preDragSnapshot.current) {
            setValue('questions', preDragSnapshot.current, { shouldDirty: true });
            preDragSnapshot.current = null;
        }
    };

    const findPageForId = id => {
        // Check if it's a droppable empty page zone
        if (typeof id === 'string' && id.startsWith('page-')) {
            return Number(id.replace('page-', ''));
        }
        return keyToPage[id] ?? 0;
    };

    const handleDragOver = ({ active, over }) => {
        if (!over) return;

        const activeKey = String(active.id);
        const overKey = String(over.id);
        const activePageIdx = findPageForId(activeKey);
        const overPageIdx = findPageForId(overKey);

        if (activePageIdx === overPageIdx) return;

        // Only update the page property on the dragged item — nothing else
        const questionsCopy = cloneDeep(questions);
        if (!questionsCopy[activeKey]) return;

        questionsCopy[activeKey] = questionsCopy[activeKey].map(q => ({
            ...q,
            page: overPageIdx,
        }));

        setValue('questions', questionsCopy, { shouldDirty: true });
    };

    const handleDragEnd = ({ active, over }) => {
        if (!over) {
            handleDragCancel();
            return;
        }
        preDragSnapshot.current = null;

        const activeKey = String(active.id);
        const overKey = String(over.id);

        if (activeKey === overKey) return;

        const questionsCopy = cloneDeep(questions);
        const entries = Object.entries(questionsCopy);
        const oldIndex = entries.findIndex(([k]) => k === activeKey);
        if (oldIndex === -1) return;

        const reindex = arr =>
            arr.reduce((acc, [, value], i) => {
                acc[i] = value;
                return acc;
            }, {});

        let reindexed;
        if (overKey.startsWith('page-')) {
            // Dropped on empty page — page already set by handleDragOver, just re-index by page order
            const byPage = {};
            entries.forEach(([key, langs]) => {
                const p = langs[0]?.page || 0;
                if (!byPage[p]) byPage[p] = [];
                byPage[p].push([key, langs]);
            });
            const ordered = [];
            const pageCount = pages?.length || 1;
            for (let p = 0; p < pageCount; p++) {
                if (byPage[p]) ordered.push(...byPage[p]);
            }
            reindexed = reindex(ordered);
        } else {
            const newIndex = entries.findIndex(([k]) => k === overKey);
            if (newIndex === -1) return;

            const [moved] = entries.splice(oldIndex, 1);
            entries.splice(newIndex, 0, moved);
            reindexed = reindex(entries);
        }

        // Strip conditions that reference questions no longer before them in order
        const reindexedEntries = Object.entries(reindexed);
        const titleToKey = {};
        reindexedEntries.forEach(([key, langs]) => {
            const title = langs[0]?.title;
            if (title) titleToKey[title] = Number(key);
        });
        reindexedEntries.forEach(([key, langs]) => {
            const keyNum = Number(key);
            langs.forEach(q => {
                if (!q.conditions) return;
                q.conditions = q.conditions.filter(c => {
                    const sourceKey = titleToKey[c.questionTitle];
                    return sourceKey !== undefined && sourceKey < keyNum;
                });
                if (q.conditions.length === 0) q.conditions = null;
            });
        });

        setValue('questions', reindexed, { shouldDirty: true });
    };

    // ── Render helpers ──

    const renderQuestionItem = (key, questionLangs) => {
        const visibleLangs = visibleQuestionLanguages(questionLangs, languageSet);
        const question = visibleLangs.find(l => l.language === currentLocale) || visibleLangs[0];
        const isNewQuestion = !!questionLangs[0]?._isNew;
        const previousTitles = getPreviousQuestionTitles(questions, key);
        return (
            <QuestionItem
                index={key}
                isView={isView}
                question={question}
                isOpen={questionLangs[0]?.isOpen}
                hasError={visibleLangs.some(q => !isQuestionValid(q, previousTitles))}
                isTitleLocked={!isNewQuestion}
                onClick={onClickQuestion}
                onChange={onChangeQuestion}
                onChangeRequired={onChangeRequired}
                onChangeIsGate={onChangeIsGate}
                onChangeConditions={onChangeConditions}
                onDelete={onDeleteQuestion}
                onOptionsChange={onOptionsChange}
                setLocalizeModal={setLocalizeModal}
                isLocalizationDisabled={languageSet?.length === 1}
                previousQuestions={getPreviousFilterableQuestions(key)}
            />
        );
    };

    const hasQuestions = dndEntries.length > 0;
    const isDisabled = isView || !satisfactionSurveyEnabled;

    return (
        <>
            {!hasQuestions ? (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '16px',
                        padding: '48px 16px',
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)', maxWidth: 320 }}>
                        {t('FeedbackSurveys.questionsDescription')}
                    </Typography>
                    <AddDropdownHeader
                        disabled={isDisabled}
                        disabledTooltip={!satisfactionSurveyEnabled ? t('FeedbackSurveys.enableNpsToAddQuestions') : ''}
                        options={QUESTION_TYPE_OPTIONS}
                        onAdd={onAddQuestion}
                        buttonText={t('FeedbackSurveys.addQuestion')}
                        buttonProps={{ size: 'small' }}
                    />
                </Box>
            ) : (
                <div style={{ marginTop: 8, marginBottom: 24 }}>
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleDragStart}
                        onDragOver={handleDragOver}
                        onDragEnd={handleDragEnd}
                        onDragCancel={handleDragCancel}
                    >
                        {(pages || [{}]).map((_, pageIdx) => {
                            const pageQuestions = questionsByPage[pageIdx] || [];
                            const pageKeys = pageQuestions.map(([key]) => key);

                            return (
                                <div key={pageIdx}>
                                    {multiplePages && (
                                        <PageDivider pageIndex={pageIdx} isView={isView} onDelete={onDeletePage} />
                                    )}
                                    {pageQuestions.length === 0 ? (
                                        <DroppableEmptyZone
                                            id={`page-${pageIdx}`}
                                            label={t('FeedbackSurveys.emptyPageHint')}
                                        />
                                    ) : (
                                        <SortableContext
                                            id={`page-${pageIdx}`}
                                            items={pageKeys}
                                            strategy={verticalListSortingStrategy}
                                        >
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                {pageQuestions.map(([key, questionLangs]) => (
                                                    <SortableItem key={key} id={key}>
                                                        {renderQuestionItem(key, questionLangs)}
                                                    </SortableItem>
                                                ))}
                                            </div>
                                        </SortableContext>
                                    )}
                                </div>
                            );
                        })}
                    </DndContext>
                    {!isView && satisfactionSurveyEnabled && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, marginTop: 2 }}>
                            <AddDropdownHeader
                                disabled={isDisabled}
                                options={QUESTION_TYPE_OPTIONS}
                                onAdd={onAddQuestion}
                                buttonText={t('FeedbackSurveys.addQuestion')}
                                buttonProps={{ size: 'small' }}
                            />
                            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={onAddPage}>
                                {t('FeedbackSurveys.addPage')}
                            </Button>
                        </Box>
                    )}
                </div>
            )}
            {localizeModal.open ? (
                <LocalizeItemModal
                    modal={localizeModal}
                    onClose={() => setLocalizeModal({ open: false, index: null, ctx: null })}
                    currentLocale={currentLocale}
                    entity="questions"
                    propName="question"
                    withChangeTitle={false}
                    withChangeEntity
                    languages={languageOptions}
                />
            ) : null}
            {deleteModal.open && (
                <ModalConfirm
                    isOpen={deleteModal.open}
                    handleClose={() => setDeleteModal({ open: false, key: null })}
                    title={t('common.deleteTitle')}
                    description={t('common.deleteDescription')}
                    onPrimaryAction={onConfirmDelete}
                    primaryActionText={t('buttons.delete')}
                    primaryActionType="danger"
                />
            )}
        </>
    );
};

export default Questions;
